/**
 * DisputeHistoryViewer - Displays compiled dispute context and evidentiary history
 *
 * DevStory 4.5:
 * Criterio 6 - Registro automático del contexto e histórico
 * Criterio 10 - Publicación respetando la exclusión de datos confidenciales
 * Criterio 12 - Preparación del expediente para la DAO
 */

import type { Dispute, DisputeHistory } from '@/types';
import { sanitizeUrl } from '@/lib/urlSanitizer';

interface DisputeHistoryViewerProps {
  dispute: Dispute;
  history: DisputeHistory;
}

export function DisputeHistoryViewer({
  history,
}: DisputeHistoryViewerProps): JSX.Element {
  return (
    <div className="space-y-6">
      {/* Deliverable and Rejections Timeline */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Deliverables */}
        <div className="space-y-4 rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-5">
          <h4 className="flex items-center gap-2 text-sm font-bold text-white">
            <i className="ri-folder-zip-line text-[var(--state-brand-active,#36d399)]" />
            Submitted Deliverables & Versions
          </h4>

          {history.deliverables && history.deliverables.length > 0 ? (
            <div className="space-y-3">
              {history.deliverables.map((item, idx) => (
                <div
                  key={idx}
                  className="space-y-2 rounded-lg border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] p-3 text-xs"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-white">
                      Version {item.version || '1.0'}
                    </span>
                    <span className="text-[var(--text-dark-secondary,rgba(255,255,255,0.5))]">
                      {item.submittedAt
                        ? new Date(item.submittedAt).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  </div>
                  {item.documentation && (
                    <div className="flex items-center gap-1.5 truncate">
                      <i className="ri-file-text-line text-blue-400" />
                      <a
                        href={sanitizeUrl(item.documentation) ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        className="truncate text-blue-400 hover:underline"
                      >
                        {item.documentation}
                      </a>
                    </div>
                  )}
                  {item.links && (
                    <div className="flex items-center gap-1.5 truncate">
                      <i className="ri-link text-[var(--state-brand-active,#36d399)]" />
                      <a
                        href={sanitizeUrl(item.links) ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        className="truncate text-[var(--state-brand-active,#36d399)] hover:underline"
                      >
                        {item.links}
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.5))]">
              No previous deliverable attachments on record.
            </p>
          )}
        </div>

        {/* Prior Rejections & Motives */}
        <div className="space-y-4 rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-5">
          <h4 className="flex items-center gap-2 text-sm font-bold text-white">
            <i className="ri-history-line text-rose-400" />
            Milestone Rejections & Stated Reasons
          </h4>

          {history.rejections && history.rejections.length > 0 ? (
            <div className="space-y-3">
              {history.rejections.map((rej, idx) => (
                <div
                  key={idx}
                  className="space-y-1 rounded-lg border border-rose-500/20 bg-rose-500/5 p-3 text-xs"
                >
                  <div className="flex items-center justify-between text-rose-300">
                    <span className="font-semibold">
                      Rejected by {rej.rejectedByName || 'Client'}
                    </span>
                    <span className="text-[var(--text-dark-secondary,rgba(255,255,255,0.5))]">
                      {rej.rejectedAt
                        ? new Date(rej.rejectedAt).toLocaleString()
                        : 'N/A'}
                    </span>
                  </div>
                  <p className="pt-1 text-white">{rej.reason}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.5))]">
              No rejection history recorded.
            </p>
          )}
        </div>
      </div>

      {/* Domain Event Audit Trail */}
      <div className="space-y-4 rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-5">
        <h4 className="flex items-center justify-between text-sm font-bold text-white">
          <span className="flex items-center gap-2">
            <i className="ri-git-commit-line text-amber-400" />
            Domain Event Audit Trail (Event-Driven Architecture)
          </span>
          <span className="text-[11px] font-normal text-[var(--text-dark-secondary,rgba(255,255,255,0.6))]">
            Dispute Domain Events
          </span>
        </h4>

        {history.events && history.events.length > 0 ? (
          <div className="relative space-y-4 pl-6 before:absolute before:bottom-2 before:left-2 before:top-2 before:w-px before:bg-[var(--base-border,#3d3d3d)]">
            {history.events.map((ev, idx) => (
              <div key={ev.id || idx} className="relative space-y-1 text-xs">
                <span className="absolute -left-6 top-1 h-2.5 w-2.5 rounded-full bg-amber-400 ring-4 ring-[var(--base-surface-2,#231f1f)]" />
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold text-white">
                    {ev.eventType}
                  </span>
                  <span className="text-[11px] text-[var(--text-dark-secondary,rgba(255,255,255,0.5))]">
                    {new Date(ev.createdAt).toLocaleString()}
                  </span>
                </div>
                {ev.eventData && Object.keys(ev.eventData).length > 0 && (
                  <pre className="overflow-x-auto rounded border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] p-2 text-[10px] text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                    {JSON.stringify(ev.eventData, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.5))]">
            No events logged yet.
          </p>
        )}
      </div>
    </div>
  );
}
